import { useContext, useEffect, useState } from 'react'
import { isEqual } from 'lodash'
import {
  JobsLocationsDocument,
  JobsLocationsQuery,
  JobsLocationsQueryVariables,
  LocaleEnum,
  Salary_Brackets,
} from 'generated/apolloComponents'
import useTranslation from 'next-translate/useTranslation'
import Checkbox from '../../atoms/Checkbox'
import MultiSelect, { SelectDataType } from '../../atoms/Select/Multi'
import CustomRange from '../../atoms/Range'
import Toggle from '../../atoms/Toggle'
import {
  ActionType,
  JobListContext,
  SelectType,
} from '@/context/jobListContext'
import {
  roles,
  skills,
  experienceLevel,
  Skills,
  employmentTypes,
} from '@/data/jobData'
import {
  arrayOfObjectToStringArrayByKey,
  arrayToObject,
  technologies,
} from '@/helpers/utils'
import JobFiltersRwdWrapper from './JobFiltersRwdWrapper'
import RawSelect from '@/components/atoms/Select/RawSelect'
import { initializeApollo } from '@/api/apollo'
import Radio from '@/components/atoms/Radio'
import { filterWithSearchTerms, filterFromStart } from '@/helpers/jobUtils'
import { useCountry } from '@/context/countryContext'

interface iProps {
  jobsLocations: Array<object>
  slugState: any
  totalCount: number
  isVisible: boolean
  setIsVisible: (value: boolean) => void
  handleOrderChange: (value: SelectDataType | null, key: string) => void
}

const JobFilters = ({
  jobsLocations,
  slugState,
  totalCount,
  isVisible,
  setIsVisible,
  handleOrderChange,
}: iProps) => {
  const MIN_SALARY = 0
  const MAX_SALARY = 50000
  const { state, dispatch } = useContext(JobListContext)
  const [isVisibleOrder, setIsVisibleOrder] = useState(false)
  const [currentSkills, setCurrentSkills] = useState<SelectType[] | undefined>(
    skills.general
  )
  const { t, lang } = useTranslation('common')
  const apolloClient = initializeApollo({})
  const [locations, setLocations] = useState(jobsLocations)
  const { country } = useCountry()

  const handleSelectChange = (value: SelectDataType | null, key: string) => {
    dispatch({
      type: ActionType.ChangeFilter,
      payload: { ...state.filters, [key]: value },
    })
  }

  const handleCheckboxChange = (value: string[], key: string) => {
    dispatch({
      type: ActionType.ChangeFilter,
      payload: { ...state.filters, [key]: value },
    })
  }

  const handleRadioChange = (value: string, key: string) => {
    dispatch({
      type: ActionType.ChangeFilter,
      payload: { ...state.filters, [key]: value },
    })
  }

  const handleRangeChange = (value: number[] | []) => {
    let tmp: Salary_Brackets | [] = []

    if (!isEqual(value, [MIN_SALARY, MAX_SALARY])) {
      tmp = { min: value[0], max: value[1] }
      dispatch({
        type: ActionType.ChangeFilter,
        payload: { ...state.filters, salaryBrackets: tmp },
      })
    } else {
      dispatch({
        type: ActionType.ChangeFilter,
        payload: { ...state.filters, salaryBrackets: undefined },
      })
    }
  }

  const handleToggleChange = (value: boolean, key: string) => {
    if (value === undefined && JSON.stringify(state.filters) == '{}') {
      return
    }
    dispatch({
      type: ActionType.ChangeFilter,
      payload: { ...state.filters, [key]: value },
    })
  }

  useEffect(() => {
    if (slugState?.filters?.city) {
      const slugLocations = slugState?.filters?.city

      const results = jobsLocations.filter(
        // @ts-ignore
        ({ value: id1 }) => !slugLocations.some(({ value: id2 }) => id2 === id1)
      )
      setLocations(
        Object.values([...results, ...slugLocations]).flat() as SelectType[]
      )
    }
  }, [])

  useEffect(() => {
    if (slugState?.filters?.role) {
      const parsedRoles = arrayOfObjectToStringArrayByKey(
        slugState?.filters?.role,
        'value'
      )

      const filtered = Object.keys(skills)
        .filter((key) => parsedRoles.includes(key))
        .reduce(
          (obj, key) => ({
            ...obj,
            [key]: skills[key as keyof Skills],
          }),
          {}
        )
      const filteredSkills = Object.values(filtered).flat()
      setCurrentSkills(
        (filteredSkills.length
          ? filteredSkills
          : skills.general) as SelectType[]
      )
    } else {
      setCurrentSkills(skills.general)
    }
  }, [slugState?.filters?.role])

  const handleVisibleChange = (e: any) => {
    e.preventDefault()
    setIsVisible(!isVisible)
  }

  const handleVisibleOrder = (e: any) => {
    e.preventDefault()
    e.stopPropagation()
    setIsVisibleOrder(!isVisibleOrder)
  }

  const handleClearFilters = (e: any) => {
    dispatch({
      type: ActionType.ChangeFilter,
      payload: { experienceLevel: [], city: [], role: [], skills: [] },
    })
    setIsVisible(false)
  }

  const onOrderChange = (value: SelectDataType | null) => {
    handleOrderChange(value, 'order')
    // setIsVisibleOrder(false);
  }

  const remoteLocationsData = async () => {
    const jobsLocationsData = await apolloClient.query<
      JobsLocationsQuery,
      JobsLocationsQueryVariables
    >({
      query: JobsLocationsDocument,
      variables: {
        amount: 100,
        language: LocaleEnum[lang as 'pl' | 'en'],
        country: country,
      },
    })
    return arrayToObject(jobsLocationsData.data.jobsLocations as string[])
  }

  const remoteSkillsData = async () => {
    const technologiesData = technologies
    return arrayToObject(technologiesData as string[])
  }

  return (
    <JobFiltersRwdWrapper
      handleVisibleChange={handleVisibleChange}
      handleClearFilters={handleClearFilters}
      isVisible={isVisible}
      totalCount={totalCount}
    >
      <div>
        <form>
          <div className='flex flex-wrap justify-between'>
            <RawSelect
              className='w-full my-3'
              data={locations}
              filterOption={filterFromStart}
              name='city'
              onSelect={handleSelectChange}
              remoteData={remoteLocationsData}
              state={state}
              title={t('common:localization')}
              value={state?.filters?.city}
            />
            <Radio
              onChange={handleRadioChange}
              name='locationDistance'
              value={slugState?.filters?.locationDistance || '30'}
              options={[
                { label: '+5km', value: '5' },
                { label: '+10km', value: '10' },
                { label: '+30km', value: '30' },
                { label: '+50km', value: '50' },
                { label: '+100km', value: '100' },
              ]}
            />

            <MultiSelect
              addNew={false}
              className='w-full my-3'
              filterOption={filterWithSearchTerms}
              isSearchable={false}
              name='role'
              onSelect={handleSelectChange}
              state={state}
              title={t('common:position')}
              value={slugState?.filters?.role}
              data={roles.map((item) => ({
                ...item,
                label: t(`common:role_${item.value}`),
              }))}
            />
            <RawSelect
              className='w-full my-3'
              data={currentSkills}
              filterOption={filterFromStart}
              name='skills'
              onSelect={handleSelectChange}
              remoteData={remoteSkillsData}
              state={state}
              title={t('common:technology')}
              value={slugState?.filters?.skills}
            />
          </div>
          <div className='flex flex-wrap md:mt-8 justify-between'>
            <div className='py-6 w-full'>
              <h4 className='text-sm font-semibold mb-1'>
                {t('common:experience_level')}
              </h4>
              <Checkbox
                onChange={handleCheckboxChange}
                name='experienceLevel'
                value={slugState?.filters?.experienceLevel}
                options={experienceLevel}
              />
            </div>
            <div className='border-b border-gray-100 py-6 w-full'>
              <h4 className='text-sm font-semibold mb-1'>
                {t('common:employment_type')}
              </h4>
              <Checkbox
                onChange={handleCheckboxChange}
                name='employmentType'
                value={slugState?.filters?.employmentType}
                options={employmentTypes}
              />
            </div>
            <div className='pt-6 pb-4 w-full'>
              <h4 className='text-sm font-semibold mb-1'>
                {t('common:brackets')}
              </h4>
              <CustomRange
                name='salaryBrackets'
                value={slugState?.filters?.salaryBrackets}
                onChange={handleRangeChange}
                min={MIN_SALARY}
                max={MAX_SALARY}
                step={1000}
                className='w-full'
              />
            </div>
          </div>
          <div className='flex flex-wrap mt-4 justify-start'>
            <Toggle
              onChange={handleToggleChange}
              value={slugState?.filters?.withSalary}
              name='withSalary'
              title={t('common:brackets_only')}
              className='mr-5 md:mr-8 border-b border-gray-100 md:border-0 mt-6 lg:mt-0 md:mr-24'
            />
          </div>
        </form>
      </div>
    </JobFiltersRwdWrapper>
  )
}

export default JobFilters
